import { createNodeRuleSet, createShapesContainerNode, createSilkShapesContainerNode, createTextShapeUnderShapesContainer, FootPrintPadHoleType, TextAlignment, } from "@buildwithflux/models";
import { metersToMm } from "@buildwithflux/shared";
// eslint-disable-next-line boundaries/element-types
import { PcbLayoutRuleCompiler } from "../../pcb/layout_engine/rule_parsing_and_compilation/PcbLayoutRuleCompiler";
import { TextPlaceholder } from "../../SharedDataModels";
import { replaceAutoWithDefault2, replaceAutoWithDefault3 } from "../utils";
import { designatorFontSize, designatorMargin, pinMarkerMargin, pinMarkerRadius, silkWidth } from "./commonConstants";
/**
 * Get the side for a pin in DIP packages (which have pins on two sides only)
 * Side 0 = left side, Side 1 = right side
 */
function getSide(i, gridX) {
    if (i < gridX) {
        return 0;
    }
    else if (i < gridX * 2) {
        return 1;
    }
    return -1;
}
function dipInferPinLayout(bakedRules, existingPadNodes) {
    const result = { grid: [], otherPads: [] };
    const withoutPinNumberPadNodes = existingPadNodes.filter((pad) => pad.terminalId === "" || pad.terminalId === undefined);
    withoutPinNumberPadNodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    const remainingPadsWithNumbers = existingPadNodes.filter((pad) => pad.terminalId !== "" && pad.terminalId !== undefined);
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const total = gridX * 2; // Two sides with gridX pins each
    for (let i = 0; i < total; i++) {
        const foundMatchingI = remainingPadsWithNumbers.findIndex((pad) => pad.terminalId === `${i + 1}`);
        const foundMatching = remainingPadsWithNumbers[foundMatchingI];
        if (foundMatchingI !== -1) {
            remainingPadsWithNumbers.splice(foundMatchingI, 1);
        }
        const matchingPad = foundMatching ?? withoutPinNumberPadNodes.shift();
        const sideId = getSide(i, gridX);
        if (matchingPad && sideId === -1) {
            result.otherPads.push(matchingPad);
        }
        else {
            const sideArr = result.grid[sideId] ?? [];
            sideArr.push(matchingPad ?? null);
            result.grid[sideId] = sideArr;
        }
    }
    result.otherPads.push(...withoutPinNumberPadNodes);
    result.otherPads.push(...remainingPadsWithNumbers);
    return result;
}
export function dipComputeInferredRules(bakedRules, config, existingPadNodes) {
    const pinSize = replaceAutoWithDefault2(bakedRules.pinSize, { x: config.pinSizeX, y: config.pinSizeY });
    const pinPitch = replaceAutoWithDefault2(bakedRules.pinPitch, { x: config.pinPitch, y: config.rowSpacing });
    const pinsCount = existingPadNodes.length;
    const padCountX = Math.ceil(pinsCount / 2); // Half the pins on each side
    const gridArrayCount = replaceAutoWithDefault2(bakedRules.gridArrayCount, {
        x: config.gridArrayCountX ?? padCountX,
        y: 0, // DIP only has pins on two sides
    });
    const packageSize = replaceAutoWithDefault3(bakedRules.packageSize, {
        x: gridArrayCount.x * config.pinPitch,
        y: config.rowSpacing,
        z: config.packageHeight,
    });
    const newBakedRules = {
        ...bakedRules,
        packageSize,
        pinSize,
        pinPitch,
        gridArrayCount,
    };
    return { ...newBakedRules, pinLayout: dipInferPinLayout(newBakedRules, existingPadNodes) };
}
export function dipGeneratePadRules(footprintNodeUid, bakedRules) {
    const result = {};
    const pinSizeX = bakedRules.pinSize?.x ?? 0;
    const pinSizeY = bakedRules.pinSize?.y ?? 0;
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const pinPitchX = bakedRules.pinPitch?.x ?? 0;
    const rowSpacing = bakedRules.pinPitch?.y ?? 0;
    const totalX = (gridX - 1) * pinPitchX;
    const pinLayout = bakedRules.pinLayout;
    if (!pinLayout)
        return result;
    // Standard DIP hole diameter is 0.8mm for 0.3" and 0.6" packages
    const holeDiameter = 0.8 / 1000;
    for (let sideId = 0; sideId < pinLayout.grid.length; sideId++) {
        const sideArr = pinLayout.grid[sideId];
        if (!sideArr)
            continue;
        for (let i = 0; i < sideArr.length; i++) {
            const matchingPad = sideArr[i];
            if (!matchingPad)
                continue;
            let posX = 0;
            let posY = 0;
            if (sideId === 0) {
                posX = -rowSpacing / 2;
                posY = -(i * pinPitchX - totalX / 2);
            }
            else if (sideId === 1) {
                posX = rowSpacing / 2;
                posY = i * pinPitchX - totalX / 2;
            }
            const selector = PcbLayoutRuleCompiler.createUidAttributeSelector(matchingPad.type, matchingPad.uid);
            const ruleSet = {
                uid: matchingPad.uid,
                selector: selector,
                rules: {},
                generatedFromAncestorNodeUid: footprintNodeUid,
            };
            result[ruleSet.uid] = ruleSet;
            const rules = {
                position: `${metersToMm(posX)}mm ${metersToMm(posY)}mm`,
                size: `${metersToMm(pinSizeX)}mm ${metersToMm(pinSizeY)}mm`,
                padHoleType: FootPrintPadHoleType.platedThroughHole,
                holeSize: `${metersToMm(holeDiameter)}mm`,
            };
            ruleSet.rules = createNodeRuleSet(rules);
        }
    }
    return result;
}
export function dipGenerateAdditionalPads(_footprintNodeUid, _bakedRules) {
    return [];
}
export function dipGenerateSilkNodes(footprintNodeUid, bakedRules) {
    if (!bakedRules.packageSize)
        return [];
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const pinPitchX = bakedRules.pinPitch?.x ?? 0;
    const totalX = (gridX - 1) * pinPitchX;
    const rowSpacing = bakedRules.pinPitch?.y ?? 0;
    const result = [];
    const shapesContainerNode = createShapesContainerNode(footprintNodeUid);
    result.push(shapesContainerNode);
    const silkPaths = [];
    // Silkscreen coordinates must match pad placement:
    // - X axis = row spacing direction (left/right rows)
    // - Y axis = pin pitch direction (pins along the row)
    // Silkscreen should not intersect with copper pads, so we need clearance
    // The pads are centered at +/- rowSpacing/2, so we need to account for pad width
    const pinSizeX = bakedRules.pinSize?.x ?? 0;
    const silkClearance = 0.2 / 1000; // 0.2mm clearance from pads
    // Silkscreen width = distance between inner edges of pads minus clearance
    const silkWidth_X = rowSpacing - pinSizeX - silkClearance * 2;
    const silkHeight_Y = bakedRules.packageSize.x;
    // Draw package outline with notch at top center (positive Y edge)
    // Pin 1 is at upper left (negative X, positive Y) based on pad placement
    const notchRadius = 0.5 / 1000; // 0.5mm notch radius
    // Package outline - draw with notch cutout at Pin 1 end (visual top)
    // SVG coordinates: positive Y goes DOWN, so visual "top" is NEGATIVE Y
    // Pin 1 is at visual top-left, so notch should be at negative Y edge
    // Arc sweep-flag=1 makes the arc curve inward (concave) when going from positive X to negative X
    silkPaths.push(`<path d="
              M ${-silkWidth_X / 2} ${silkHeight_Y / 2}
              L ${silkWidth_X / 2} ${silkHeight_Y / 2}
              L ${silkWidth_X / 2} ${-silkHeight_Y / 2}
              L ${notchRadius} ${-silkHeight_Y / 2}
              A ${notchRadius} ${notchRadius} 0 0 1 ${-notchRadius} ${-silkHeight_Y / 2}
              L ${-silkWidth_X / 2} ${-silkHeight_Y / 2}
              Z" stroke-width="${silkWidth}"
              stroke-linecap="round" fill="none" />`);
    // Pin 1 marker dot inside the silkscreen at Pin 1 corner (visual top-left)
    // In SVG coords: top-left is (negative X, negative Y)
    // Position it inside the package outline, offset from the corner
    const pin1MarkerOffset = pinMarkerRadius * 2;
    silkPaths.push(`<circle
              cx="${-silkWidth_X / 2 + pin1MarkerOffset}"
              cy="${-silkHeight_Y / 2 + pin1MarkerOffset}"
              r="${pinMarkerRadius}" fill="#000000" />`);
    const svgNode = createSilkShapesContainerNode(shapesContainerNode.uid, footprintNodeUid, silkPaths);
    result.push(svgNode);
    const designatorTextNode = createTextShapeUnderShapesContainer("Designator", shapesContainerNode.uid, `${shapesContainerNode.uid}_designator`, footprintNodeUid);
    designatorTextNode.pcbNodeRuleSet = createNodeRuleSet({
        position: `${metersToMm(0)}mm ${metersToMm(silkHeight_Y / 2 + designatorMargin)}mm`,
        textAlign: TextAlignment.center,
        content: `= ${TextPlaceholder.elementDesignator}`,
        fontSize: `${metersToMm(designatorFontSize)}mm`,
    }, true);
    result.push(designatorTextNode);
    return result;
}
/**
 * Generate silkscreen nodes for SIP (Single In-line Package) packages.
 * SIP packages have a single row of pins, so the silkscreen is drawn differently
 * than DIP packages which have two rows.
 */
export function sipGenerateSilkNodes(footprintNodeUid, bakedRules) {
    if (!bakedRules.packageSize)
        return [];
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const pinPitchX = bakedRules.pinPitch?.x ?? 0;
    const totalPinSpan = (gridX - 1) * pinPitchX;
    const packageHeight = bakedRules.packageSize.z ?? 0;
    const result = [];
    const shapesContainerNode = createShapesContainerNode(footprintNodeUid);
    result.push(shapesContainerNode);
    const silkPaths = [];
    // For SIP packages:
    // - Pins are in a single row along the Y axis (at X = 0)
    // - Package body extends in the +X direction from the pins
    // - Silkscreen should outline the package body without overlapping pads
    const pinSizeX = bakedRules.pinSize?.x ?? 0;
    const pinSizeY = bakedRules.pinSize?.y ?? 0;
    const silkClearance = 0.2 / 1000; // 0.2mm clearance from pads
    // Package body dimensions
    // Width (X direction): package extends from pins, use packageHeight as body width
    const bodyWidth = packageHeight;
    // Height (Y direction): spans all pins plus some margin
    const bodyHeight = totalPinSpan + pinSizeY + silkClearance * 2;
    // Position the body so it doesn't overlap with pads
    // Pads are centered at X = 0, so body starts at X = pinSizeX/2 + clearance
    const bodyOffsetX = pinSizeX / 2 + silkClearance;
    // Draw a simple rectangular outline for the package body
    // The body is offset in +X direction from the pin row
    silkPaths.push(`<path d="
              M ${bodyOffsetX} ${-bodyHeight / 2}
              L ${bodyOffsetX + bodyWidth} ${-bodyHeight / 2}
              L ${bodyOffsetX + bodyWidth} ${bodyHeight / 2}
              L ${bodyOffsetX} ${bodyHeight / 2}
              Z" stroke-width="${silkWidth}"
              stroke-linecap="round" fill="none" />`);
    // Pin 1 marker - small dot near pin 1 position (at negative Y end)
    const pin1MarkerX = bodyOffsetX + pinMarkerMargin + pinMarkerRadius;
    const pin1MarkerY = -totalPinSpan / 2 + pinMarkerMargin + pinMarkerRadius;
    silkPaths.push(`<circle cx="${pin1MarkerX}" cy="${pin1MarkerY}" r="${pinMarkerRadius}" fill="#000000" />`);
    const svgNode = createSilkShapesContainerNode(shapesContainerNode.uid, footprintNodeUid, silkPaths);
    result.push(svgNode);
    // Designator text above the package
    const designatorTextNode = createTextShapeUnderShapesContainer("Designator", shapesContainerNode.uid, `${shapesContainerNode.uid}_designator`, footprintNodeUid);
    designatorTextNode.pcbNodeRuleSet = createNodeRuleSet({
        position: `${metersToMm(bodyOffsetX + bodyWidth / 2)}mm ${metersToMm(-bodyHeight / 2 - designatorMargin)}mm`,
        textAlign: TextAlignment.center,
        content: `= ${TextPlaceholder.elementDesignator}`,
        fontSize: `${metersToMm(designatorFontSize)}mm`,
    }, true);
    result.push(designatorTextNode);
    return result;
}
//# sourceMappingURL=dip.js.map